/**
 * TopHeader — Modern Enterprise Header
 *
 * Clean, minimal fixed header with refined spacing.
 * Inspired by Linear and Vercel's top navigation.
 */

import React, { useState, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  IconButton,
  Avatar,
  Typography,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Menu as MenuIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  SettingsBrightness as SystemIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Palette as PaletteIcon,
  HelpOutline as HelpIcon,
  KeyboardArrowDown,
  PhoneAndroid as PhoneAndroidIcon,
} from '@mui/icons-material';
import { useDesignSystemStore } from '../store/useDesignSystemStore';
import { TOPBAR_HEIGHT, SIDEBAR_EXPANDED, SIDEBAR_COLLAPSED, BP_MOBILE, BP_LAPTOP } from '../theme/tokens';
import { useAuth } from '../../context/AuthContext';
import { useOrgSettings } from '../../context/OrgSettingsContext';
import Logo from '../../components/Logo';
import CompanyLogo from '../../components/CompanyLogo';
import GlobalSearch from '../../components/GlobalSearch';
import QuickCreate from '../overlays/QuickCreate';
import NotificationPanel from '../overlays/NotificationPanel';

// ── Theme mode icon ───────────────────────────────────────
function ThemeModeIcon({ mode }) {
  if (mode === 'dark') return <DarkModeIcon sx={{ fontSize: 17 }} />;
  if (mode === 'system') return <SystemIcon sx={{ fontSize: 17 }} />;
  return <LightModeIcon sx={{ fontSize: 17 }} />;
}

function TopHeader() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const orgSettings = useOrgSettings();

  const mode = useDesignSystemStore((s) => s.mode);
  const toggleMode = useDesignSystemStore((s) => s.toggleMode);
  const sidebarCollapsed = useDesignSystemStore((s) => s.sidebarCollapsed);
  const mobileDrawerOpen = useDesignSystemStore((s) => s.mobileDrawerOpen);
  const setMobileDrawerOpen = useDesignSystemStore((s) => s.setMobileDrawerOpen);

  const isMobile = useMediaQuery(`(max-width:${BP_MOBILE - 1}px)`);
  const isDesktop = useMediaQuery(`(min-width:${BP_LAPTOP}px)`);

  const [profileAnchor, setProfileAnchor] = useState(null);
  const profileOpen = Boolean(profileAnchor);

  const handleProfileOpen = useCallback((e) => setProfileAnchor(e.currentTarget), []);
  const handleProfileClose = useCallback(() => setProfileAnchor(null), []);

  const handleLogout = useCallback(async () => {
    handleProfileClose();
    await logout();
    navigate('/login');
  }, [logout, navigate]);

  const handleNavTo = useCallback((path) => {
    handleProfileClose();
    navigate(path);
  }, [navigate]);

  const avatarInitial = user?.name?.[0]?.toUpperCase() || 'U';
  const firstName = user?.name?.split(' ')[0] || 'User';
  const modeLabel = mode === 'dark' ? 'Dark' : mode === 'system' ? 'Auto' : 'Light';

  const logoAreaWidth = isMobile ? 'auto' : (sidebarCollapsed ? SIDEBAR_COLLAPSED : SIDEBAR_EXPANDED) - 16;

  return (
    <Box
      component="header"
      role="banner"
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: TOPBAR_HEIGHT,
        zIndex: theme.zIndex.drawer + 1,
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        px: { xs: 1.5, sm: 2 },
        gap: 1,
        backdropFilter: 'blur(8px)',
        backgroundColor: (theme) =>
          theme.palette.mode === 'dark'
            ? 'rgba(20, 24, 37, 0.92)'
            : 'rgba(255, 255, 255, 0.92)',
      }}
    >
      {/* ─── Left: Hamburger + Logo ───────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: { xs: 'auto', sm: logoAreaWidth },
          flexShrink: 0,
          transition: 'width 200ms cubic-bezier(0.4,0,0.2,1)',
          overflow: 'hidden',
        }}
      >
        {isMobile && (
          <IconButton
            onClick={() => setMobileDrawerOpen(!mobileDrawerOpen)}
            aria-label="Open navigation menu"
            size="small"
            sx={{ mr: 1, color: 'text.secondary' }}
          >
            <MenuIcon sx={{ fontSize: 20 }} />
          </IconButton>
        )}
        <Logo size="md" />
        <CompanyLogo size="md" sx={{ ml: 1.5 }} />
      </Box>

      {/* ─── Center: Global Search ────────────────────────────── */}
      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', mx: 1 }}>
        <GlobalSearch />
      </Box>

      {/* ─── Right: Actions ───────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
        <QuickCreate />

        <Tooltip title="Download Mobile App">
          <IconButton
            component="a"
            href="/api/download/app"
            download="ICTAPP.apk"
            aria-label="Download ICT Ops mobile app"
            size="small"
            sx={{
              width: 32,
              height: 32,
              color: 'text.secondary',
              '&:hover': { color: 'primary.main' },
            }}
          >
            <PhoneAndroidIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>

        <NotificationPanel />

        <Tooltip title={`Theme: ${modeLabel}`}>
          <IconButton
            onClick={toggleMode}
            aria-label={`Toggle theme (${modeLabel})`}
            size="small"
            sx={{
              width: 32,
              height: 32,
              color: 'text.secondary',
              '&:hover': { color: 'text.primary' },
            }}
          >
            <ThemeModeIcon mode={mode} />
          </IconButton>
        </Tooltip>

        {/* Organization badge (desktop) */}
        {isDesktop && orgSettings?.orgName && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 1,
              py: 0.25,
              borderRadius: '6px',
              border: '1px solid',
              borderColor: 'divider',
              ml: 0.5,
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontSize: '0.6875rem', fontWeight: 500, color: 'text.secondary' }}
            >
              {orgSettings.orgName}
            </Typography>
          </Box>
        )}

        {/* ─── Profile ─────────────────────────────────────── */}
        <Tooltip title="Account">
          <Box
            role="button"
            tabIndex={0}
            aria-label="Open account menu"
            aria-haspopup="true"
            aria-expanded={profileOpen}
            onClick={handleProfileOpen}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleProfileOpen(e)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              ml: 0.75,
              px: 0.75,
              py: 0.5,
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 150ms ease',
              '&:hover': { bgcolor: 'action.hover' },
              '&:focus-visible': {
                outline: `2px solid ${theme.palette.primary.main}`,
                outlineOffset: '1px',
              },
            }}
          >
            <Avatar
              sx={{
                width: 28,
                height: 28,
                fontSize: '0.7rem',
                fontWeight: 600,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                color: '#fff',
              }}
            >
              {avatarInitial}
            </Avatar>
            {!isMobile && (
              <>
                <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                  <Typography
                    sx={{ fontSize: '0.8125rem', fontWeight: 500, lineHeight: 1.2, color: 'text.primary' }}
                  >
                    {firstName}
                  </Typography>
                </Box>
                <KeyboardArrowDown
                  sx={{ fontSize: 14, color: 'text.disabled', display: { xs: 'none', md: 'block' } }}
                />
              </>
            )}
          </Box>
        </Tooltip>

        {/* Profile Dropdown */}
        <Menu
          anchorEl={profileAnchor}
          open={profileOpen}
          onClose={handleProfileClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{ sx: { mt: 1, minWidth: 200 } }}
        >
          <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>{user?.name}</Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{user?.email}</Typography>
            <Typography
              sx={{
                fontSize: '0.6875rem',
                color: 'text.disabled',
                mt: 0.25,
                textTransform: 'capitalize',
              }}
            >
              {user?.role?.replace(/_/g, ' ')}
            </Typography>
          </Box>

          <Box sx={{ py: 0.5 }}>
            <MenuItem onClick={() => handleNavTo('/profile')}>
              <ListItemIcon><PersonIcon fontSize="small" sx={{ color: 'text.secondary' }} /></ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontSize: '0.8125rem' }}>Profile</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleNavTo('/theme-settings')}>
              <ListItemIcon><PaletteIcon fontSize="small" sx={{ color: 'text.secondary' }} /></ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontSize: '0.8125rem' }}>Appearance</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleNavTo('/help')}>
              <ListItemIcon><HelpIcon fontSize="small" sx={{ color: 'text.secondary' }} /></ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontSize: '0.8125rem' }}>Help</ListItemText>
            </MenuItem>
          </Box>

          <Divider />

          <Box sx={{ py: 0.5 }}>
            <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
              <ListItemIcon><LogoutIcon fontSize="small" color="error" /></ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontSize: '0.8125rem', color: 'error.main' }}>
                Sign out
              </ListItemText>
            </MenuItem>
          </Box>
        </Menu>
      </Box>
    </Box>
  );
}

export default memo(TopHeader);
